import { Avatar, Group, Menu, rem, Stack, UnstyledButton, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useRouter } from '@tanstack/react-router';
import { clsx as cx } from 'clsx';

import { ChevronDown, Logout, ProfileEditIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { Footnote } from '@apple/ui/typography/components/footnote';
import { Headline } from '@apple/ui/typography/components/headline';

import { useAuthContext } from '@apple/features/auth/hooks/useAuthContext';

import classes from './AvatarMenu.module.css';
import { ToggleColorSchemeMenuItem } from './ToggleColorSchemeMenuItem';

export function AvatarMenu({
	fullWidth = false,
	variant,
	compact = false,
}: {
	fullWidth?: boolean;
	variant: 'light' | 'dark';
	compact?: boolean;
}) {
	const { t } = useTranslation('common');
	const auth = useAuthContext();
	const router = useRouter();
	const [open, openActions] = useDisclosure(false);
	const theme = useMantineTheme();

	if (!auth.profile) {
		return null;
	}

	const fullDisplayName = `${auth.profile.firstName} ${auth.profile.lastName}`;

	return (
		<Menu
			width={rem(260)}
			position='bottom-end'
			offset={compact ? 2 : undefined}
			transitionProps={{ transition: 'pop-top-right' }}
			onClose={openActions.close}
			onOpen={openActions.open}
			closeOnEscape
			closeOnClickOutside
			withinPortal
			data-testid='avatar-menu'
		>
			<Menu.Target>
				<UnstyledButton
					w={fullWidth ? '100%' : undefined}
					p={compact ? rem(4) : rem(8)}
					className={cx(
						classes.user,
						open && variant === 'light' ? classes.activeLightBg : undefined,
						open && variant === 'dark' ? classes.activeDarkBg : undefined,
					)}
				>
					<Group gap={5} wrap='nowrap' justify='space-between'>
						<Group gap={5} wrap='nowrap'>
							<Avatar
								title={fullDisplayName}
								alt={fullDisplayName}
								radius='xl'
								color={variant === 'light' ? theme.black : theme.white}
								size={compact ? rem(26) : rem(42)}
							/>
							{!compact && (
								<UserName
									firstName={auth.profile.firstName}
									lastName={auth.profile.lastName}
									roles={auth.profile.roles}
								/>
							)}
						</Group>
						{!compact && <ChevronDown size={24} />}
					</Group>
				</UnstyledButton>
			</Menu.Target>

			<Menu.Dropdown className={classes.menuDropdown}>
				<Menu.Label
					c='bright'
					bg={compact ? (variant === 'dark' ? 'dark.7' : 'gray.0') : undefined}
				>
					{compact ? (
						<UserName
							firstName={auth.profile.firstName}
							lastName={auth.profile.lastName}
							roles={auth.profile.roles}
						/>
					) : (
						t('shell.settings')
					)}
				</Menu.Label>
				<Menu.Item
					onClick={() => void router.navigate({ to: '/profile' })}
					leftSection={<ProfileEditIcon />}
				>
					{t('shell.accountSettings')}
				</Menu.Item>
				<ToggleColorSchemeMenuItem />
				<Menu.Divider />
				<Menu.Item
					onClick={() => void router.navigate({ to: '/logout' })}
					leftSection={<Logout />}
				>
					{t('shell.logout')}
				</Menu.Item>
			</Menu.Dropdown>
		</Menu>
	);
}

function UserName({
	firstName,
	lastName,
	roles,
}: {
	firstName: string;
	lastName: string;
	roles: string[];
}) {
	return (
		<Stack gap={0} justify='flex-start' align='flex-start'>
			<Headline bold>
				{firstName} {lastName}
			</Headline>
			<Footnote c='dimmed'>{roles}</Footnote>
		</Stack>
	);
}
